import { AxiosResponse } from "axios";
import { push } from "connected-react-router";
import * as R from "ramda";
import { Action, ActionFunction1 } from "redux-actions";
import { call, put, select } from "redux-saga/effects";
import { Creators as AllCreators } from "../../../../actions";
import {
  exportAllAssessments,
  generateRowAllAssessments,
  IAssessmentReportDataAction,
  usersAnalyticsToTotal,
} from "../../../../actions/export-assessments";
import {
  exportAllUsersData,
  generateRows as generateUserRows,
  IUserReportDataAction,
} from "../../../../actions/export-users";
import { SagaFunction } from "../../../../actions/types";
import { IImportReport } from "../../../../api/CourseAuthorApi";
import { AxiosInterceptedResult } from "../../../../api/interceptors";
import {
  IAlias,
  IDisplayInfoResponse,
  IOrganizationTree,
  IResolveResponse,
  ResolvableType,
} from "../../../../api/ResolveApi/resolve-api.types";
import {
  IClientPatch,
  IPatchAction,
  IPatchPayload,
  IPatchResponse,
  IPatchSubstAction,
} from "../../../../components/course-author/helpers/patches";
import { patchPayloadCreator } from "../../../../components/course-author/helpers/patches/creators";
import { dependencies } from "../../../../dependencies";
import {
  analyticsCompactUnitSummaryGetResponseToCompactUnitSummaryUIData,
  analyticsCourseInstanceSummaryGetResponseToCourseInstanceSummaryUIData,
} from "../../../../parsers/analytics";
import {
  IFullOrganization,
  IFullScormPackage,
  IOrganizationState,
  IStateResponse,
  toTuple,
} from "../../../../parsers/resolve";
import { IOrganization } from "../../../../reducers/course-author/types";
import { SagaAction } from "../../../../sagas/types";
import * as selectors from "../../../../selectors/selectors";
import { Err, Ok, Result } from "../../../../utils/result";
import { IChangeOrganizationName, ICompleteOrganization } from "../../../types/common";
import {
  makeDeleteOrganizationPatch,
  modifyOrganizationTitlePatch,
  toggleDeleteOrganizationPatch,
} from "../helpers/patches";
import { rootOrgId } from "../helpers/root";
import {
  IFetchInitialDataFurtherActions,
  IMiddleError,
  IOrganizationSettingsFurtherActions,
  IOrganizationSettingsImportFurtherActions,
  IRequestAllTableDataFurtherActions,
  ISettingsFile,
} from "../types";
import * as actions from "./index";

const Creators = (AllCreators as any).App.SelectOrganization;
const { api } = dependencies;

type OnFailure = ActionFunction1<{ message: string }, Action<{ message: string }>>;

function* getOrganization(onFailure: OnFailure) {
  const orgId: string = yield select(selectors.organizationId);
  const accountId: string = yield select(selectors.accountId);

  const [fetchError, response]: AxiosInterceptedResult<Array<IResolveResponse<IOrganizationState>>> = yield call(
    api.resolve.organization.post,
    accountId,
    [orgId]
  );

  if (fetchError || !response) {
    yield put(onFailure({ message: "Failed to find organization" }));
    return;
  }

  const organizationFound: ICompleteOrganization = {
    organizationId: orgId,
    versionId: response.data[0].value.versionId,
    ...response.data[0].value.state,
  };

  if (!organizationFound) {
    yield put(onFailure({ message: "Failure: Empty response" }));
    return;
  }

  return (yield organizationFound) as ICompleteOrganization;
}

export const fetchSettings: (fa: IFetchInitialDataFurtherActions) => SagaFunction = fa =>
  function* _fetchSettings(_: Action<void>): SagaAction {
    const orgId: string = yield select(selectors.organizationId);
    const accountId: string = yield select(selectors.accountId);
    const orgFound: ICompleteOrganization = yield getOrganization(fa.onFailure);

    if (!orgFound) {
      return;
    }

    const [adminsErr, adminsResp]: AxiosInterceptedResult<Array<IResolveResponse<IDisplayInfoResponse>>> = yield call(
      api.resolve.displayInfo.post,
      accountId,
      orgFound.admins.map(toTuple),
      orgId
    );

    if (adminsErr || !adminsResp) {
      yield put(fa.onFailure({ message: "Could not get admins data" }));
      return;
    }

    const admins = R.zip(orgFound.admins, adminsResp.data).map(([accId, resolveData]) => ({
      accountId: accId,
      displayName: resolveData.value.displayName || "Unknown account",
    }));

    yield put(
      fa.onSuccess({
        admins,
        organization: orgFound,
      })
    );
  };

// NOTE: This is very similar to patchSettings we should look for a way to
// mix it together if possible.
export const deleteOrganization: (patchFA: IOrganizationSettingsFurtherActions) => SagaFunction = patchFA =>
  function* _deleteOrganization(): SagaAction {
    const currentOrg: ICompleteOrganization = yield select(selectors.completeOrganization);
    const accountId: string = yield select(selectors.accountId);
    const patch = toggleDeleteOrganizationPatch(currentOrg, accountId);

    const patchPayload: IPatchPayload = yield call(patchPayloadCreator, currentOrg.versionId, patch);

    const [patchError]: AxiosInterceptedResult<IPatchResponse[]> = yield call(
      api.organizations.patchOrganization,
      accountId,
      currentOrg.organizationId,
      patchPayload
    );

    if (patchError) {
      yield put(patchFA.onFailure({ message: "Failed to patch organization" }));
      return;
    }

    const [resolveErr, resolveRes]: AxiosInterceptedResult<Array<
      IResolveResponse<IStateResponse<IFullOrganization>>
    >> = yield call(api.resolve.organization.post, accountId, [rootOrgId]);

    if (resolveErr || !resolveRes || !resolveRes.data) {
      yield put(
        patchFA.onFailure({
          message: "Failed to fetch organization to redirect after delete",
        })
      );
      return;
    }
    const fullOrg = resolveRes.data[0].value;

    const org: IOrganization = {
      defaultLocale: fullOrg.state.defaultLocale,
      displayName: fullOrg.state.displayName || "Unknown org",
      organizationId: rootOrgId,
      ownerOrganizationId: fullOrg.state.owner,
      releaseLine: fullOrg.state.releaseLine,
      style: null,
    };

    const { window, saltcomplianceLocalStorageKeys } = dependencies;
    yield call(
      [window.localStorage, window.localStorage.setItem],
      saltcomplianceLocalStorageKeys.organizationId,
      rootOrgId
    );
    yield put(Creators.selectOrganization(org));
    yield put(Creators.handleSelectOrganizationSuccess(org));
    yield put(push("/dashboard"));
  };

export const patchSettings: (fa: IOrganizationSettingsFurtherActions) => SagaFunction = fa =>
  function* _patchSettings(action: Action<IClientPatch<IPatchAction>>): SagaAction {
    const currentOrg: ICompleteOrganization = yield select(selectors.completeOrganization);
    const accountId: string = yield select(selectors.accountId);

    const patchPayload: IPatchPayload = yield call(patchPayloadCreator, currentOrg.versionId, action.payload);

    const [patchError]: AxiosInterceptedResult<IPatchResponse[]> = yield call(
      api.organizations.patchOrganization,
      accountId,
      currentOrg.organizationId,
      patchPayload
    );

    if (patchError) {
      yield put(fa.onFailure({ message: "Failed to patch organization" }));
      return;
    }

    yield put(actions.refreshSettings());
    yield put(fa.onSuccess(currentOrg));
  };

export const patchImports: (fa: IOrganizationSettingsImportFurtherActions) => SagaFunction = fa =>
  function* _patchImports({ payload }: Action<ISettingsFile>): SagaAction {
    const currentOrg: ICompleteOrganization = yield select(selectors.completeOrganization);
    const accountId: string = yield select(selectors.accountId);

    const [uploadError, uploadResponse]: AxiosInterceptedResult<IImportReport> = yield call(
      api.organizations.patchOrgImports,
      accountId,
      currentOrg.organizationId,
      payload.file,
      payload.uploadType
    );

    if (uploadError) {
      yield put(
        fa.onFailure({
          message: `There was an error uploading the file with name "${payload.file.name}": ${uploadError ||
            "Unknown"}`,
          uploadType: payload.uploadType,
        })
      );
      return;
    }

    const importReport = (uploadResponse as AxiosResponse<IImportReport>).data;

    if (importReport.parsingFailure) {
      yield put(
        fa.onFailure({
          message: importReport.parsingFailure,
          uploadType: payload.uploadType,
        })
      );
      return;
    }

    yield put(fa.onSuccess(importReport));
  };

type Table<Header = {}, Row = {}> = Array<Header[] | Row>;

const withAllUsersAnalyticsData: <Header, Row>(
  handleUsersAnalytics: (usersAnalytics: IUserReportDataAction) => Table<Header, Row>,
  handleAllUsersAnalytics: (usersAnalytics: Table<Header, Row>) => Generator
) => (fa: IRequestAllTableDataFurtherActions) => SagaFunction = (handleUsersAnalytics, handleAllUsersAnalytics) => fa =>
  function* _requestAllUsersAnalyticsData(_: Action<void>): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const organizationId: string = yield select(selectors.organizationId);
    const organizationName: string = yield select(selectors.organizationName);
    const [scoIdsError, scoIdsResp]: AxiosInterceptedResult<string[]> = yield call(
      api.scormPackages.getEditableByRelativeTo,
      accountId,
      organizationId
    );

    if (scoIdsError || !scoIdsResp) {
      yield put(
        fa.onFatalError({
          message: "Could not get the list of scorm packages to process",
        })
      );
      return;
    }

    const scormPackageIds = scoIdsResp.data;
    // NOTE: We're using a for loop instead of map for the sake of performance (#335).
    const results: Table<any, any> = [];

    yield put(fa.onStep({ stepNumber: 0, totalSteps: scormPackageIds.length }));
    for (let idx = 0; idx < scormPackageIds.length; idx++) {
      const result: IUserReportDataAction | null = yield _getUserReportDataByScormPackage(
        accountId,
        scormPackageIds[idx],
        organizationName,
        fa.onMiddleError
      );
      if (result) {
        results.push.apply(results, handleUsersAnalytics(result));
      }
      yield put(fa.onStep({ stepNumber: idx + 1, totalSteps: scormPackageIds.length }));
    }
    yield handleAllUsersAnalytics(results);
    yield put(fa.onFinish());
  };

export const requestAllUserData = withAllUsersAnalyticsData(generateUserRows, exportAllUsersData);

export const requestAllAssessmentData = withAllUsersAnalyticsData(
  ua => [generateRowAllAssessments(computeAssessmentFromUserData(ua))],
  exportAllAssessments
);

const computeAssessmentFromUserData = (reportData: IUserReportDataAction): IAssessmentReportDataAction => ({
  assessmentsTotal: usersAnalyticsToTotal(reportData.users, reportData.usersAnalytics),
  courseDisplayName: reportData.courseDisplayName,
  objectDisplayName: reportData.objectDisplayName,
  organizationName: reportData.organizationName,
  packageState: reportData.packageState,
  type: reportData.type,
});

export function capitalizeFirstLetter(str: string): string {
  const firstLetterUpperCaseString = str.charAt(0).toUpperCase() + str.slice(1);
  return firstLetterUpperCaseString;
}

function* _getUserReportDataByScormPackage(
  accountId: string,
  scormPackageId: string,
  organizationName: string,
  onMiddleError: ActionFunction1<IMiddleError, Action<IMiddleError>>
): SagaAction<any, IUserReportDataAction | null> {
  // Resolve scorm package
  const [scormPackagesError, scormPackagesResp]: AxiosInterceptedResult<Array<
    IResolveResponse<IStateResponse<IFullScormPackage>>
  >> = yield call(api.resolve.scormPackage.post, accountId, [scormPackageId]);

  if (scormPackagesError || !scormPackagesResp || !scormPackagesResp.data) {
    yield put(
      onMiddleError({
        message: "Could not get information about scorm package",
        scormPackageId,
        scormPackageName: null,
      })
    );
    return null;
  }

  const scormPackage = scormPackagesResp.data[0];
  const mkMiddleError = (message: string) => ({
    message,
    scormPackageId,
    scormPackageName: scormPackage.value.state.name,
  });

  // Resolve course display information
  const [courseError, courseResp]: AxiosInterceptedResult<Array<
    IResolveResponse<IDisplayInfoResponse>
  >> = yield call(api.resolve.displayInfo.post, accountId, [[ResolvableType.Course, scormPackage.value.state.course]]);

  if (courseError || !courseResp || !courseResp.data) {
    yield put(onMiddleError(mkMiddleError("Could not get course name")));
    return null;
  }

  // Get all users for scorm package
  const [usersError, usersResponse]: AxiosInterceptedResult<Array<[string, string]>> = yield call(
    api.users.getAllForScormPackage,
    accountId,
    "dummyCourseId",
    scormPackageId
  );

  if (usersError || !usersResponse) {
    yield put(onMiddleError(mkMiddleError("Could not get scorm package users")));
    return null;
  }

  // Get summary set
  const [setError, setResponse]: AxiosInterceptedResult<{
    summarySetId: number;
  }> = yield call(api.analytics.getScormPackageSummarySet, accountId, "dummyCourseId", scormPackageId);

  if (setError || !setResponse) {
    yield put(onMiddleError(mkMiddleError("Could not get scorm package setId")));
    return null;
  }

  // Get instance and unit summaries
  const setId = setResponse.data.summarySetId;
  const [courseInstancesError, courseInstancesResponse] = yield call(
    api.analytics.getScormPackageCourseInstancesSummaries,
    accountId,
    "dummyCourseId",
    scormPackageId,
    setId
  );
  const [compactUnitError, compactUnitResponse] = yield call(
    api.analytics.getScormPackageCompactUnitSummaries,
    accountId,
    "dummyCourseId",
    scormPackageId,
    setId
  );

  if (compactUnitError || courseInstancesError || !courseInstancesResponse) {
    yield put(onMiddleError(mkMiddleError("Could not get scorm package instance or unit responses")));
    return null;
  }

  const courseInstanceData = analyticsCourseInstanceSummaryGetResponseToCourseInstanceSummaryUIData(
    courseInstancesResponse.data
  );
  const compactUnitData = analyticsCompactUnitSummaryGetResponseToCompactUnitSummaryUIData(compactUnitResponse.data);
  const usersAnalytics = {};
  Object.entries(courseInstanceData).forEach(([k, v]) => {
    usersAnalytics[k] = { ...v, ...compactUnitData[v.unitSummaryId] };
  });

  const courseDisplayName = courseResp.data[0].value.displayName || "Unknown course";
  const objectDisplayName = scormPackage.value.state.name;
  const packageState = capitalizeFirstLetter(scormPackage.value.state.state);
  const users = usersResponse.data.map(data => ({
    accountId: data[0],
    displayName: data[1],
  }));

  return {
    courseDisplayName,
    objectDisplayName,
    organizationName,
    packageState,
    type: "DUMMY_TYPE", // Needed to typecheck with IAction
    users,
    usersAnalytics,
  };
}

interface IFetchOrgPreviewFurtherActions {
  onSuccess: ActionFunction1<IOrganizationTree, Action<IOrganizationTree>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

interface IGetOrganizationInfoFurtherActions {
  onSuccess: ActionFunction1<IOrganizationTree | null, Action<IOrganizationTree | null>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

interface IDeleteOrganizationFurtherActions {
  onSuccess: ActionFunction1<IOrganizationTree | null, Action<IOrganizationTree | null>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

export interface IOrgIdStruct {
  children: IOrgIdStruct[];
  value: string;
}

/**
 * Given a parent organization, crete a structure containing the ids of all the organization children.
 * @param {string} accountId - Id of the account requesting the info.
 * @param {string} orgId - Id of the parent organization.
 * @return {Promise<Result<IOrgIdStruct, string>>} - Wrapper that contains either the structure of the organization ids,
 * or an error message.
 */
export const createOrganizationIdStructure = async (
  accountId: string,
  orgId: string
): Promise<Result<IOrgIdStruct, string>> => {
  // request children ids
  const [err, res]: AxiosInterceptedResult<string[]> = await api.organizations.getEditableByRelativeTo(
    accountId,
    orgId
  );

  if (err) {
    return Err(err);
  }
  if (res === null) {
    return Err("Could not fetch organization id");
  }

  const orgStructRes: Array<Result<IOrgIdStruct, string>> = await Promise.all(
    res.data.filter(id => id !== orgId).map(async id => await createOrganizationIdStructure(accountId, id))
  );
  const errors: string = orgStructRes
    .reduce((acc: string[], current: Result<IOrgIdStruct, string>) => {
      if (!current.ok && current.error) {
        acc.push(current.error);
      }
      return acc;
    }, [])
    .join("\n");

  if (errors.length > 0) {
    return Err(errors);
  }

  const children: IOrgIdStruct[] = orgStructRes.reduce((acc: IOrgIdStruct[], current: Result<IOrgIdStruct, string>) => {
    if (current.ok) {
      acc.push(current.value);
    }
    return acc;
  }, []);

  return Ok({ children, value: orgId });
};

/**
 * Create an organization tree given an organization id structure and the info of each organization.
 * @param {IOrgIdStruct} orgIdStruct - Organization id structure.
 * @param {Map<string, IDisplayInfoResponse>} dataMap - Map with the information of each organization in the structure.
 * @return {IOrganizationTree} - Organization structure containing all the information of each organization.
 */
export const createOrganizationTreeStructure = (
  orgIdStruct: IOrgIdStruct,
  dataMap: Map<string, IDisplayInfoResponse>
): Result<IOrganizationTree, string> => {
  const organization: IDisplayInfoResponse | undefined = dataMap.get(orgIdStruct.value);

  if (organization === undefined) {
    return Err("There is no information for the organization " + orgIdStruct.value + ".");
  }
  const childrenResult = orgIdStruct.children.map(org => createOrganizationTreeStructure(org, dataMap));

  const errorStr = childrenResult
    .reduce((acc: string[], current: Result<IOrganizationTree, string>) => {
      if (!current.ok && current.error) {
        acc.push(current.error);
      }
      return acc;
    }, [])
    .join(",\r\n");
  if (errorStr.length > 0) {
    return Err(errorStr);
  }

  const children: IOrganizationTree[] = childrenResult.reduce(
    (acc: IOrganizationTree[], current: Result<IOrganizationTree, string>) => {
      if (current.ok) {
        acc.push(current.value);
      }
      return acc;
    },
    []
  );

  return Ok({
    children,
    organizationId: orgIdStruct.value,
    ...organization,
  });
};

/**
 * Get the ids of all the organizations inside the organization structure
 * @param {IOrgIdStruct} orgIdStruct - Organization structure
 * @return {string[]} - An array containing all the organization ids
 */
export const getOrganizationsIds = (orgIdStruct: IOrgIdStruct): string[] =>
  [orgIdStruct.value].concat(R.unnest(orgIdStruct.children.map(getOrganizationsIds)));

/**
 * Create a map with the information of each organization.
 * @param {string[]} orgIds - A list with the organization ids.
 * @param {Array<IResolveResponse<IDisplayInfoResponse> | null>} data - An array with the responses from the resolver call.
 * @return {Map<string, IDisplayInfoResponse>} - A map pairing the organization id with its information.
 */
export const createOrganizationInfoMap = (
  orgIds: string[],
  data: Array<IResolveResponse<IDisplayInfoResponse> | null>
): Map<string, IDisplayInfoResponse> =>
  new Map(
    R.zipWith((id: string, infoData: IResolveResponse<IDisplayInfoResponse>) => [id, infoData.value], orgIds, data)
  );

/**
 * Add information to an organization tree.
 * @param {IOrganizationTree} orgTree - The organization tree / structure.
 * @param {Map<string, IDisplayInfoResponse>} orgInfoMap  - A map containing the ids of the organizations as keys,
 * and the info of the organization as value.
 * @return {IOrganizationTree} - Return the organization tree with the given info
 */
export const addInfoToOrganizationTree = (
  orgTree: IOrganizationTree,
  orgInfoMap: Map<string, IDisplayInfoResponse>
): IOrganizationTree => {
  const orgInfo: IDisplayInfoResponse | undefined = orgInfoMap.get(orgTree.organizationId);

  if (orgInfo === undefined) {
    return orgTree;
  }

  return {
    canonicalAlias: orgInfo.canonicalAlias,
    children: orgTree.children.map(child => addInfoToOrganizationTree(child, orgInfoMap)),
    deleted: orgInfo.deleted,
    displayName: orgInfo.displayName,
    locked: orgInfo.locked,
    organizationId: orgTree.organizationId,
    owner: orgInfo.owner,
  };
};

export const fetchOrganizationPreview: (fa: IFetchOrgPreviewFurtherActions) => SagaFunction = fa =>
  function* _fetchOrganizationPreview(_: Action<void>): SagaAction {
    // get the credentials for the first api call
    const accountId: string = yield select(selectors.accountId);
    const organizationId: string = yield select(selectors.organizationId);

    const result = yield call(createOrganizationTree, accountId, organizationId);

    if (!result.ok) {
      yield put(fa.onFailure({ message: result.error }));
    }

    yield put(fa.onSuccess(result.value));
  };

export const createOrganizationTree = async (
  accountId: string,
  organizationId: string
): Promise<Result<IOrganizationTree, string>> => {
  const result = await createOrganizationIdStructure(accountId, organizationId); // fallible

  if (!result.ok) {
    return Err(result.error);
  }

  const orgIds = getOrganizationsIds(result.value);

  // create a list of pairs ('org': id) that the api can understand
  const orgIdPairStructure = orgIds.map(id => [ResolvableType.Organization, id] as [ResolvableType, string]);

  // Request organizations info of each organization
  const [infoErr, infoRes]: AxiosInterceptedResult<Array<IResolveResponse<
    IDisplayInfoResponse
  > | null>> = await api.resolve.displayInfo.post(accountId, orgIdPairStructure, organizationId);

  if (infoErr) {
    return Err(infoErr);
  }

  if (!infoRes || infoRes.data === null) {
    return Err("Could not get organizations information.");
  }

  const infoMap: Map<string, IDisplayInfoResponse> = createOrganizationInfoMap(orgIds, infoRes.data);

  const organizationTreeResult = createOrganizationTreeStructure(result.value, infoMap);

  if (!organizationTreeResult.ok) {
    return Err(organizationTreeResult.error);
  }

  return Ok(organizationTreeResult.value);
};

interface ICreateNewOrganization {
  onSuccess: ActionFunction1<IOrganizationTree, Action<IOrganizationTree>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

export const createOrganizationTreeNode = (
  organizationId: string = "orgId",
  children: IOrganizationTree[] = [],
  displayName: string | null = "New Organisation",
  owner: string = "Owner",
  locked: boolean = false,
  canonicalAlias: IAlias = { kind: "", name: "" },
  deleted: boolean = false
): IOrganizationTree => ({
  canonicalAlias,
  children,
  deleted,
  displayName,
  locked,
  organizationId,
  owner,
});

export const changeOrganizationNodeName = (
  orgTree: IOrganizationTree,
  organizationId: string,
  name: string
): IOrganizationTree => {
  if (orgTree.organizationId === organizationId) {
    orgTree.displayName = name;
    return orgTree;
  }

  orgTree.children = orgTree.children.map(child => changeOrganizationNodeName(child, organizationId, name));
  return orgTree;
};

export const insertOrganizationTreeNode = (
  orgTree: IOrganizationTree | null,
  node: IOrganizationTree
): IOrganizationTree => {
  if (orgTree === null) {
    return node;
  }

  if (orgTree.organizationId === node.owner) {
    orgTree.children.push(node);
    return orgTree;
  }

  orgTree.children = orgTree.children.map(child => insertOrganizationTreeNode(child, node));
  return orgTree;
};

export const createNewOrganization: (fa: ICreateNewOrganization) => SagaFunction = fa =>
  function* _createNewOrganization(action: Action<IFullOrganization>): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const organizationId: string = yield select(selectors.organizationId);

    // Call the api to add the new organization
    const [err, res] = yield call(api.organizations.createNewOrganization, accountId, organizationId, action.payload);

    if (err) {
      return yield put(fa.onFailure({ message: err }));
    }

    const orgTreeNode = createOrganizationTreeNode(
      res.data.value,
      [],
      action.payload.displayName,
      action.payload.owner,
      undefined,
      undefined,
      action.payload.deleted
    );

    const orgTree = R.clone(insertOrganizationTreeNode(yield select(selectors.organizationPreview), orgTreeNode));

    return yield put(fa.onSuccess(orgTree));
  };

export const createChangeOrganizationNamePatch = (
  accountId: string,
  organization: ICompleteOrganization,
  versionId: number,
  newName: string,
  oldName: string
): IPatchPayload => {
  const patch: IClientPatch<IPatchSubstAction<string>> = modifyOrganizationTitlePatch(
    oldName,
    organization,
    accountId,
    newName
  );

  return {
    baseVersionId: versionId,
    clientPatches: [patch],
  };
};

export const getOrganizationInfo = async (
  accountId: string,
  organizationId: string
): Promise<Result<ICompleteOrganization, { message: string }>> => {
  const [err, res]: AxiosInterceptedResult<Array<
    IResolveResponse<IOrganizationState>
  >> = await api.resolve.organization.post(accountId, [organizationId], organizationId);
  if (err) {
    return Err({ message: err });
  }

  if (res === null) {
    return Err({ message: "Could not get organization information." });
  }

  return Ok({
    organizationId,
    versionId: res.data[0].value.versionId,
    ...res.data[0].value.state,
  });
};

export const getOrganizationsInfo = async (
  accountId: string,
  organizationId: string,
  organizationsIds: string[]
): Promise<Result<ICompleteOrganization[], { message: string }>> => {
  const orgs: Array<[ResolvableType, string]> = organizationsIds.map(org => [ResolvableType.Organization, org]);
  const [err, res]: AxiosInterceptedResult<Array<
    IResolveResponse<IOrganizationState>
  >> = await api.resolve.organization.post(accountId, orgs, organizationId);
  if (err) {
    return Err({ message: err });
  }

  if (res === null) {
    return Err({ message: "Could not get organization information." });
  }

  const failedRequests: Array<IResolveResponse<IOrganizationState>> = res.data.filter(response => response.code >= 400);
  if (failedRequests.length) {
    return Err({ message: "Could not delete an organisation." });
  }

  const organizationsPairs: Array<R.KeyValuePair<string, IResolveResponse<IOrganizationState>>> = R.zip(
    organizationsIds,
    res.data
  );
  const organizations: ICompleteOrganization[] = organizationsPairs.map(organization => ({
    organizationId: organization[0],
    versionId: organization[1].value.versionId,
    ...organization[1].value.state,
  }));

  return Ok(organizations);
};

export const getOrganizationChildrenIds = (
  organizationStructure: IOrganizationTree,
  parentOrganizationId: string
): string[] => {
  if (organizationStructure.organizationId !== parentOrganizationId) {
    return R.unnest(
      organizationStructure.children.map(child => getOrganizationChildrenIds(child, parentOrganizationId))
    );
  }

  if (organizationStructure.children.length === 0) {
    return [parentOrganizationId];
  }

  return [
    parentOrganizationId,
    ...R.unnest(organizationStructure.children.map(child => getOrganizationChildrenIds(child, child.organizationId))),
  ];
};

export const patchMultipleOrganizations = async (
  accountId: string,
  patchZip: Array<R.KeyValuePair<string, IPatchPayload>>
): Promise<Result<boolean, { message: string }>> => {
  const responses = await Promise.all(patchZip.map(zip => patchOrganization(accountId, zip[0], zip[1])));
  const failedResponsesMessage: string = responses
    .filter(response => !response.ok)
    .map(response => (!response.ok ? response.error?.message : undefined))
    .join("\n");
  if (failedResponsesMessage) {
    return Err({ message: failedResponsesMessage });
  }
  return Ok(true);
};

export const patchOrganization = async (
  accountId: string,
  organizationId: string,
  patch: IPatchPayload
): Promise<Result<boolean, { message: string }>> => {
  // @ts-ignore
  const [err, res] = await api.organizations.patchOrganization(accountId, organizationId, patch);

  if (err) {
    return Err({ message: err });
  }

  if (res === null) {
    return Err({ message: "Could not delete the given organisation." });
  }

  return Ok(true);
};

export const deleteOrganizationFromOrganizationStructure = (
  organizationStructure: IOrganizationTree,
  organizationId: string
): IOrganizationTree | null => {
  if (organizationStructure.organizationId === organizationId) {
    return null;
  }

  const children = organizationStructure.children.map(child =>
    deleteOrganizationFromOrganizationStructure(child, organizationId)
  );
  organizationStructure.children = children.reduce((acc: IOrganizationTree[], current: IOrganizationTree | null) => {
    if (current) {
      acc.push(current);
    }
    return acc;
  }, []);
  return organizationStructure;
};

export const getOrgInfo: (fa: IGetOrganizationInfoFurtherActions) => SagaFunction = fa =>
  function* _getOrgInfo(action: Action<IChangeOrganizationName>): SagaAction {
    const organizationId: string = action.payload.organizationId;
    const accountId: string = yield select(selectors.accountId);
    const organizationInfoResult: Result<ICompleteOrganization, { message: string }> = yield call(
      getOrganizationInfo,
      accountId,
      organizationId
    );

    if (!organizationInfoResult.ok) {
      if (organizationInfoResult.error !== undefined) {
        return yield put(fa.onFailure(organizationInfoResult.error));
      }
      return yield put(
        fa.onFailure({
          message: "Could not get information for the given organization.",
        })
      );
    }

    const patch: IPatchPayload = yield call(
      createChangeOrganizationNamePatch,
      accountId,
      organizationInfoResult.value,
      organizationInfoResult.value.versionId,
      action.payload.newOrganizationName,
      action.payload.oldOrganizationName
    );

    const [err, res]: AxiosInterceptedResult<IPatchResponse[]> = yield call(
      api.organizations.patchOrganization,
      accountId,
      organizationId,
      patch
    );

    if (err) {
      yield put(fa.onFailure({ message: err }));
    }

    if (res === null) {
      yield put(
        fa.onFailure({
          message: "Could not change the name for the given organization.",
        })
      );
    }

    const organizationStructure: IOrganizationTree | null = yield select(selectors.organizationPreview);
    if (organizationStructure === null) {
      return yield put(fa.onFailure({ message: "Could not set new organizatoin name." }));
    }

    const newOrganizationStructure: IOrganizationTree = R.clone(
      changeOrganizationNodeName(organizationStructure, organizationId, action.payload.newOrganizationName)
    );

    return yield put(fa.onSuccess(newOrganizationStructure));
  };

/**
 * Delete an organization and all its children.
 */
export const deleteOrganizationFromPreview: (fa: IDeleteOrganizationFurtherActions) => SagaFunction = fa =>
  function* _deleteOrganizationFromPreview(action: Action<string>): SagaAction {
    const organizationPreviewStructure = yield select(selectors.organizationPreview);
    const accountId = yield select(selectors.accountId);
    const organizationId = action.payload;

    const orgIds = getOrganizationChildrenIds(organizationPreviewStructure, organizationId);

    const orgsInfoResult: Result<ICompleteOrganization[], { message: string }> = yield call(
      getOrganizationsInfo,
      accountId,
      organizationId,
      orgIds
    );
    if (!orgsInfoResult.ok) {
      if (orgsInfoResult.error !== undefined) {
        return yield put(fa.onFailure({ message: orgsInfoResult.error.message }));
      }
      return yield put(
        fa.onFailure({
          message: "Could not get information about the organizations.",
        })
      );
    }

    const patches: IPatchPayload[] = orgsInfoResult.value.map(orgInfoResult => ({
      baseVersionId: orgInfoResult.versionId,
      clientPatches: [makeDeleteOrganizationPatch(accountId, orgInfoResult.versionId)],
    }));

    const patchZip: Array<R.KeyValuePair<string, IPatchPayload>> = R.zip(orgIds, patches).reverse();

    const responseResults: Result<boolean, { message: string }> = yield call(
      patchMultipleOrganizations,
      accountId,
      patchZip
    );

    if (!responseResults.ok) {
      if (responseResults.error !== undefined) {
        return yield put(fa.onFailure({ message: responseResults.error.message }));
      }
      return yield put(fa.onFailure({ message: "Could not patch organization's information." }));
    }

    const organizationStructure: IOrganizationTree | null = R.clone(
      deleteOrganizationFromOrganizationStructure(organizationPreviewStructure, organizationId)
    );
    return yield put(fa.onSuccess(organizationStructure));
  };
